/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useState, useEffect, useRef } from 'react';
import {
  DialogContent,
  Grid,
  Typography,
  Button,
  IconButton,
  Container,
  TextField,
  Box,
  Stack,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'next-i18next';
import ReCAPTCHA from 'react-google-recaptcha';
import MobileDetect from 'mobile-detect';

import {
  setLocalStorageKeyeWithTTL,
  checkTTLExpiration,
} from 'lib/utils/localStroage';
import axios, { API } from 'lib/config/axios';
import { setSnackbar } from 'redux/features/ui-slice';
import useErrorsHandler from 'views/rebranded-module/common/hooks/useErrorsHandler';

import NavigatorLink from 'views/common/components/Logical/NavigatorLink';
import StyledDialog from 'views/rebranded-module/common/components/Logical/CaptureEmailOverlayV2/CaptureEmailOverlayV2.styles';
import {
  ImgBox,
  Typography700,
} from 'views/rebranded-module/common/components';
import { RiCloseFill } from 'react-icons/ri';

const { envVars } = require('next/config').default().publicRuntimeConfig;

const CaptureEmailOverlayV2 = () => {
  const dispatch = useDispatch();
  const { appState, googleAnalytics } = useSelector(
    (state) => state.globalData,
  );
  const expirationTimeInHours = appState?.settings?.show_capture_email_overlay?.expiration_time || 24;
  const handleErrorResponse = useErrorsHandler();
  const { t } = useTranslation('fe_er_common_capture_email');

  const [openOverlay, setOpenOverlay] = useState(false);
  const initialFormData = {
    name: '',
    email: '',
  };
  const [formData, setFormData] = useState(initialFormData);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const recaptchaRef = useRef();

  const handleCloseOverlay = () => {
    setOpenOverlay(false);
    setFormData(initialFormData);
  };

  const handleSendMeDeals = (value) => {
    const { email, name } = formData;
    axios
      .post(API.newslettersSubscribe, {
        authenticity_token: appState.authenticity_token,
        'g-recaptcha-response': value,
        'iv-g-recaptcha': '1',
        newsletter_subscription: {
          email,
          full_name: name,
        },
      })
      .then(() => {
        dispatch(
          setSnackbar({
            open: true,
            severity: 'success',
            message: t('fe_er_common_capture_email:subscribed_text'),
          }),
        );

        window.gtag('event', 'newsletter_signup', {
          event_category: googleAnalytics.newsLetterEventCategory,
          event_label: 'Newsletter',
          event_action: 'Lead',
        });
        handleCloseOverlay();
      })
      .catch((error) => {
        recaptchaRef.current.reset();
        switch (error.response.status) {
          case 403:
            dispatch(
              setSnackbar({
                open: true,
                severity: 'error',
                message: error.response.data.message,
              }),
            );
            break;
          case 406:
            if (error.response.data.error_code === 'ERR001') {
              dispatch(
                setSnackbar({
                  open: true,
                  severity: 'success',
                  message: t('fe_er_common_capture_email:email_exits_text'),
                }),
              );
              handleCloseOverlay();
            } else {
              dispatch(
                setSnackbar({
                  open: true,
                  severity: 'error',
                  message: error.response.data.message,
                }),
              );
            }
            break;
          default:
            handleErrorResponse(error);
        }
      });
  };

  const handleRecaptchaButtonClick = (e) => {
    e.preventDefault();
    recaptchaRef.current.execute();
  };
  let emailOverlayTimeout;
  let emailOverlayTimeoutWork = false;

  const handleEmailOverlay = (outScreen) => {
    if (outScreen && !emailOverlayTimeoutWork) {
      emailOverlayTimeoutWork = true;
      emailOverlayTimeout = setTimeout(() => {
        setOpenOverlay(true);
        setLocalStorageKeyeWithTTL(
          'hide_capture_email_overlay',
          1,
          expirationTimeInHours,
        );
        emailOverlayTimeoutWork = false;
      }, 1000);
    } else if (emailOverlayTimeoutWork) {
      clearInterval(emailOverlayTimeout);
      emailOverlayTimeoutWork = false;
    }
  };

  const handleMouseLeave = (event) => {
    if (!checkTTLExpiration('hide_capture_email_overlay')) {
      const mouseX = event.clientX;
      const mouseY = event.clientY;
      if (
        mouseY >= window.innerHeight
        || mouseX >= window.innerWidth
        || mouseY < 0
        || mouseX < 0
      ) {
        handleEmailOverlay(true);
      } else {
        handleEmailOverlay(false);
      }
    }
  };

  const handleVisibilityChange = () => {
    if (!checkTTLExpiration('hide_capture_email_overlay')) {
      setOpenOverlay(true);
      setLocalStorageKeyeWithTTL(
        'hide_capture_email_overlay',
        1,
        expirationTimeInHours,
      );
    }
  };

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const md = new MobileDetect(window.navigator.userAgent);
      const isDesktop = !md.tablet() && !md.mobile();
      if (isDesktop) {
        document.addEventListener('mouseout', handleMouseLeave);
      } else {
        document.addEventListener('visibilitychange', handleVisibilityChange);
      }

      return () => {
        if (isDesktop) {
          document.removeEventListener('mouseout', handleMouseLeave);
        } else {
          document.removeEventListener('visibilitychange', handleMouseLeave);
        }
      };
    }
  }, []);

  return (
    <StyledDialog
      open={openOverlay}
      onClose={handleCloseOverlay}
      className="CEcontainer"
      maxWidth="lg">
      <DialogContent>
        <Grid container>
          <Grid item lg={4}>
            <Box className="CEimage showDESKTOP">
              <ImgBox
                image={{
                  src: '/imgs/Rebranding/Layout/captureEmail.jpg',
                  alt: 'Capture Email Dialog',
                }}
                imgHeight="100%"
                imgWidth="100%"
                imgObjectFit="cover"
                unoptimized
              />
            </Box>
          </Grid>

          <Grid item lg={8} sm={12} className="CEcontentContainer">
            <Grid align="right" className="CEcloseBtn">
              <IconButton
                onClick={handleCloseOverlay}
                aria-label="Cancel"
                size="small">
                <RiCloseFill />
              </IconButton>
            </Grid>

            <Container className="CEOContent">
              <form onSubmit={(e) => handleRecaptchaButtonClick(e)}>
                <Stack gap={4}>
                  <Box>
                    <Typography700
                      variant="h4"
                      component="p"
                      mb={2}
                      textTransform="uppercase">
                      {t('common_layout_v2:capture_email_overlay.title')}
                    </Typography700>
                    <Typography
                      variant="body1"
                      component="p"
                      className="CEOText">
                      {t('common_layout_v2:capture_email_overlay.add_email')}
                    </Typography>
                  </Box>
                  <Box>
                    <Stack gap={2}>
                      <TextField
                        type="name"
                        required
                        fullWidth
                        variant="outlined"
                        color="secondary"
                        label={t('common_layout_v2:capture_email_overlay.name')}
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                      />
                      <TextField
                        type="email"
                        required
                        fullWidth
                        variant="outlined"
                        color="secondary"
                        label={t(
                          'common_layout_v2:capture_email_overlay.email',
                        )}
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                      />
                    </Stack>

                    {envVars.INVISIBLE_RECAPTCHA_SITE_KEY && (
                      <ReCAPTCHA
                        className="grecaptcha-badge"
                        ref={recaptchaRef}
                        sitekey={envVars.INVISIBLE_RECAPTCHA_SITE_KEY}
                        size="invisible"
                        onChange={handleSendMeDeals}
                      />
                    )}

                    <Box mt={3}>
                      <Typography variant="body2" className="CEOText">
                        <span>
                          {t(
                            'common_layout_v2:capture_email_overlay.disclaimer_text',
                          )}
                        </span>
                        {' '}
                        <NavigatorLink
                          class="CEOLink"
                          href="/rentals/rental-terms-and-conditions"
                          target="_blank">
                          <span>
                            {t(
                              'common_layout_v2:capture_email_overlay.terms_conditions',
                            )}
                          </span>
                        </NavigatorLink>
                        {' '}
                        <span>
                          {t('common_layout_v2:capture_email_overlay.and')}
                        </span>
                        {' '}
                        <NavigatorLink
                          class="CEOLink"
                          href="/privacy-policy"
                          target="_blank">
                          <span>
                            {' '}
                            {t(
                              'common_layout_v2:capture_email_overlay.privacy_policy',
                            )}
                            {' '}
                          </span>
                        </NavigatorLink>
                      </Typography>
                    </Box>
                  </Box>

                  <Box display="flex" justifyContent="flex-end">
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      className="CEOAction">
                      {t(
                        'common_layout_v2:capture_email_overlay.send_me_deals',
                      )}
                    </Button>
                  </Box>
                </Stack>
              </form>
            </Container>
          </Grid>
        </Grid>
      </DialogContent>
    </StyledDialog>
  );
};

export { CaptureEmailOverlayV2 };
